import React, { useCallback, useEffect, useRef, useState } from "react";
import "./SignPdfDrawPad.css";

const UndoIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path
      d="M8 7H15.5a5 5 0 0 1 0 10H10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M11 3.5 7 7l4 3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RedoIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path
      d="M16 7H8.5a5 5 0 0 0 0 10H14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M13 3.5 17 7l-4 3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const STROKE_SIZES = [
  { id: "s", label: "Thin", width: 2.5 },
  { id: "m", label: "Medium", width: 4.5 },
  { id: "l", label: "Thick", width: 7.5 },
];

const MAX_UNDO = 24;
const EXPORT_PAD = 10;

function canvasHasInk(canvas) {
  if (!canvas || canvas.width < 1 || canvas.height < 1) return false;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 16) {
    if (data[i] > 8) return true;
  }
  return false;
}

/** Crop transparent margins so the baked mark hugs the strokes. */
function trimInkCanvas(source, padding = EXPORT_PAD) {
  if (!source) return null;
  const ctx = source.getContext("2d");
  if (!ctx) return null;
  const { width, height } = source;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(width - 1, maxX + padding);
  maxY = Math.min(height - 1, maxY + padding);
  const tw = Math.max(1, maxX - minX + 1);
  const th = Math.max(1, maxY - minY + 1);
  const out = document.createElement("canvas");
  out.width = tw;
  out.height = th;
  const octx = out.getContext("2d");
  if (!octx) return null;
  octx.drawImage(source, minX, minY, tw, th, 0, 0, tw, th);
  return out;
}

function blobFromCanvas(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not export drawing"));
          return;
        }
        resolve(blob);
      },
      "image/png"
    );
  });
}

/**
 * Signature draw pad — transparent ink, undo/clear, exports trimmed PNG.
 */
const SignPdfDrawPad = ({
  penColor = "#111111",
  themeId = "white_grey",
  disabled = false,
  onExport = () => {},
}) => {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const exportTimerRef = useRef(null);
  const onExportRef = useRef(onExport);
  onExportRef.current = onExport;

  const [strokeId, setStrokeId] = useState("m");
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [brushCursor, setBrushCursor] = useState(null);

  const strokeWidth =
    STROKE_SIZES.find((s) => s.id === strokeId)?.width || 4.5;

  const snapshotCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    try {
      return ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch {
      return null;
    }
  }, []);

  const pushUndo = useCallback(() => {
    const snap = snapshotCanvas();
    if (!snap) return;
    const next = [...undoRef.current, snap];
    if (next.length > MAX_UNDO) next.shift();
    undoRef.current = next;
    redoRef.current = [];
    setCanUndo(next.length > 0);
    setCanRedo(false);
  }, [snapshotCanvas]);

  const scheduleExport = useCallback(() => {
    if (exportTimerRef.current) {
      clearTimeout(exportTimerRef.current);
    }
    exportTimerRef.current = setTimeout(async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ink = canvasHasInk(canvas);
      setHasInk(ink);
      if (!ink) {
        onExportRef.current(null);
        return;
      }
      try {
        const trimmed = trimInkCanvas(canvas) || canvas;
        const blob = await blobFromCanvas(trimmed);
        const file = new File([blob], "drawn-signature.png", {
          type: "image/png",
        });
        // data: URL survives modal unmount (blob: URLs get revoked on cleanup).
        const url = trimmed.toDataURL("image/png");
        onExportRef.current({
          url,
          file,
          width: trimmed.width,
          height: trimmed.height,
        });
      } catch (err) {
        console.error("[SignPdfDrawPad] export failed:", err);
        onExportRef.current(null);
      }
    }, 120);
  }, []);

  const resizeCanvas = useCallback(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;
    const rect = host.getBoundingClientRect();
    const cssW = Math.max(120, Math.floor(rect.width));
    const cssH = Math.max(120, Math.floor(rect.height));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const nextW = Math.max(1, Math.round(cssW * dpr));
    const nextH = Math.max(1, Math.round(cssH * dpr));
    if (canvas.width === nextW && canvas.height === nextH) return;

    const prev = document.createElement("canvas");
    prev.width = canvas.width;
    prev.height = canvas.height;
    prev.getContext("2d")?.drawImage(canvas, 0, 0);

    canvas.width = nextW;
    canvas.height = nextH;
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, nextW, nextH);
    if (prev.width && prev.height) {
      ctx.drawImage(prev, 0, 0, nextW, nextH);
    }
    undoRef.current = [];
    redoRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
    scheduleExport();
  }, [scheduleExport]);

  useEffect(() => {
    resizeCanvas();
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => resizeCanvas());
    ro.observe(host);
    return () => ro.disconnect();
  }, [resizeCanvas]);

  useEffect(() => {
    return () => {
      if (exportTimerRef.current) clearTimeout(exportTimerRef.current);
    };
  }, []);

  const getLocalPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const strokeAt = (from, to) => {
    const canvas = canvasRef.current;
    if (!canvas || !from || !to) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = canvas.width / Math.max(1, canvas.clientWidth || canvas.width);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(1, strokeWidth * dpr);
    ctx.strokeStyle = penColor;
    ctx.fillStyle = penColor;
    ctx.globalCompositeOperation = "source-over";
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(to.x, to.y, Math.max(1, (strokeWidth * dpr) / 2), 0, Math.PI * 2);
    ctx.fill();
  };

  const updateBrushCursor = (event) => {
    if (disabled) {
      setBrushCursor(null);
      return;
    }
    const host = hostRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    setBrushCursor({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const hideBrushCursor = () => {
    setBrushCursor(null);
  };

  const handlePointerDown = (event) => {
    if (disabled) return;
    event.preventDefault();
    updateBrushCursor(event);
    const pt = getLocalPoint(event);
    if (!pt) return;
    pushUndo();
    drawingRef.current = true;
    lastPtRef.current = pt;
    strokeAt(pt, pt);
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // ignore
    }
  };

  const handlePointerMove = (event) => {
    updateBrushCursor(event);
    if (!drawingRef.current || disabled) return;
    event.preventDefault();
    const pt = getLocalPoint(event);
    if (!pt || !lastPtRef.current) return;
    strokeAt(lastPtRef.current, pt);
    lastPtRef.current = pt;
  };

  const endStroke = (event) => {
    if (event?.type === "pointerleave" || event?.type === "pointercancel") {
      hideBrushCursor();
    } else if (event) {
      updateBrushCursor(event);
    }
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPtRef.current = null;
    scheduleExport();
  };

  const handleUndo = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const snap = undoRef.current.pop();
    if (!canvas || !snap) {
      setCanUndo(undoRef.current.length > 0);
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const current = snapshotCanvas();
    if (current) {
      const nextRedo = [...redoRef.current, current];
      if (nextRedo.length > MAX_UNDO) nextRedo.shift();
      redoRef.current = nextRedo;
    }
    ctx.putImageData(snap, 0, 0);
    setCanUndo(undoRef.current.length > 0);
    setCanRedo(redoRef.current.length > 0);
    scheduleExport();
  };

  const handleRedo = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const snap = redoRef.current.pop();
    if (!canvas || !snap) {
      setCanRedo(redoRef.current.length > 0);
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const current = snapshotCanvas();
    if (current) {
      const nextUndo = [...undoRef.current, current];
      if (nextUndo.length > MAX_UNDO) nextUndo.shift();
      undoRef.current = nextUndo;
    }
    ctx.putImageData(snap, 0, 0);
    setCanUndo(undoRef.current.length > 0);
    setCanRedo(redoRef.current.length > 0);
    scheduleExport();
  };

  const handleClear = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    pushUndo();
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    scheduleExport();
  };

  return (
    <div className="spsm-draw">
      <div
        ref={hostRef}
        className={`spsm-draw__pad spsm-draw__pad--${themeId}${
          disabled ? " is-disabled" : ""
        }`}
      >
        <div
          className="spsm-draw__toolbar"
          role="toolbar"
          aria-label="Draw tools"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="spsm-draw__sizes" role="group" aria-label="Stroke size">
            {STROKE_SIZES.map((size) => (
              <button
                key={size.id}
                type="button"
                className={`spsm-draw__size${
                  strokeId === size.id ? " is-selected" : ""
                }`}
                title={size.label}
                aria-label={size.label}
                aria-pressed={strokeId === size.id}
                disabled={disabled}
                onClick={() => setStrokeId(size.id)}
              >
                <span
                  className="spsm-draw__size-dot"
                  style={{ width: size.width + 2, height: size.width + 2 }}
                />
              </button>
            ))}
          </div>
          <span className="spsm-draw__divider" aria-hidden="true" />
          <div className="spsm-draw__actions">
            <button
              type="button"
              className="spsm-draw__btn spsm-draw__btn--icon"
              disabled={disabled || !canUndo}
              onClick={handleUndo}
              title="Undo"
              aria-label="Undo"
            >
              <UndoIcon />
            </button>
            <button
              type="button"
              className="spsm-draw__btn spsm-draw__btn--icon"
              disabled={disabled || !canRedo}
              onClick={handleRedo}
              title="Redo"
              aria-label="Redo"
            >
              <RedoIcon />
            </button>
            <button
              type="button"
              className="spsm-draw__btn"
              disabled={disabled || !hasInk}
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </div>

        <canvas
          ref={canvasRef}
          className="spsm-draw__canvas"
          aria-label="Draw your signature"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerEnter={updateBrushCursor}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
        />
        {brushCursor && !disabled ? (
          <span
            className="spsm-draw__brush"
            aria-hidden="true"
            style={{
              left: brushCursor.x,
              top: brushCursor.y,
              width: Math.max(8, strokeWidth + 4),
              height: Math.max(8, strokeWidth + 4),
            }}
          />
        ) : null}
        {!hasInk ? (
          <p className="spsm-draw__hint" aria-hidden="true">
            Draw here with mouse or finger
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default SignPdfDrawPad;
